import React, { useState } from 'react';
import { Mail, Lock, User, BookOpen, Star } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { Button, Input, Card } from '../ui';
import { getAuthErrorMessage, getValidationErrorMessage } from '../../utils/errorHandler';

interface SignupFormProps {
    onToggleMode: () => void;
}

const SignupForm: React.FC<SignupFormProps> = ({ onToggleMode }) => {
    const [formData, setFormData] = useState({
        displayName: '',
        email: '',
        password: '',
        confirmPassword: '',
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const { signUp } = useAuth();

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value,
        });
    };


    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        if (formData.password !== formData.confirmPassword) {
            setError(getValidationErrorMessage('confirmPassword', formData.password));
            setLoading(false);
            return;
        }

        const passwordError = getValidationErrorMessage('password', formData.password);
        if (passwordError) {
            setError(passwordError);
            setLoading(false);
            return;
        }

        try {
            await signUp(formData.email, formData.password, formData.displayName);
        } catch (error: any) {
            setError(getAuthErrorMessage(error));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="w-full max-w-md mx-auto">
            {/* Islamic School Header */}
            <div className="text-center mb-8">
                <div className="flex items-center justify-center mb-4">
                    <div className="relative">
                        <div className="w-16 h-16 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl flex items-center justify-center shadow-lg animate-glow">
                            <BookOpen className="h-8 w-8 text-white" />
                        </div>
                        <div className="absolute -top-1 -right-1 w-6 h-6 bg-gradient-to-br from-yellow-400 to-yellow-500 rounded-full flex items-center justify-center animate-float">
                            <Star className="h-3 w-3 text-white" />
                        </div>
                    </div>
                </div>
                <h2 className="text-3xl font-bold gradient-text mb-2">Join Our Community</h2>
                <p className="text-gray-600 font-medium">Create your administrator account</p>
                <div className="mt-2 text-sm text-emerald-600 font-medium">Noor-e-Hamza Islamic College</div>
            </div>

            <Card glassy={true} className="islamic-pattern">
                <form onSubmit={handleSubmit} className="space-y-6">
                    {error && (
                        <div className="bg-red-50/80 backdrop-blur-sm border border-red-200 text-red-600 px-4 py-3 rounded-xl shadow-sm">
                            {error}
                        </div>
                    )}

                    <Input
                        label="Full Name"
                        name="displayName"
                        type="text"
                        value={formData.displayName}
                        onChange={handleChange}
                        placeholder="Enter your full name"
                        icon={<User className="h-5 w-5" />}
                        required
                    />

                    <Input
                        label="Email Address"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="Enter your email"
                        icon={<Mail className="h-5 w-5" />}
                        required
                    />

                    <Input
                        label="Password"
                        name="password"
                        type="password"
                        value={formData.password}
                        onChange={handleChange}
                        placeholder="Enter your password"
                        icon={<Lock className="h-5 w-5" />}
                        showPasswordToggle={true}
                        required
                    />

                    <Input
                        label="Confirm Password"
                        name="confirmPassword"
                        type="password"
                        value={formData.confirmPassword}
                        onChange={handleChange}
                        placeholder="Confirm your password"
                        icon={<Lock className="h-5 w-5" />}
                        showPasswordToggle={true}
                        required
                    />

                    <Button
                        type="submit"
                        loading={loading}
                        fullWidth
                        size="lg"
                        className="mt-8"
                    >
                        {loading ? 'Creating Account...' : 'Create Account'}
                    </Button>
                </form>

                <div className="mt-8 pt-6 border-t border-white/20 text-center">
                    <p className="text-gray-600 font-medium">
                        Already have an account?{' '}
                        <button
                            onClick={onToggleMode}
                            className="gradient-text hover:underline font-semibold transition-all duration-200"
                        >
                            Sign in here
                        </button>
                    </p>
                </div>
            </Card>

        </div>
    );
};

export default SignupForm;
